import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles, CreditCard, ShieldCheck, Crown, Star, Globe, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ProPortalHeader from '@/components/ProPortalHeader';
import { useMyPlan } from '@/hooks/useMyPlan';
import { formatApiError } from '@/utils/apiError';

async function post(path, body) {
  const r = await fetch(`${API_URL}/api${path}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
}

const ICON = { free: Sparkles, web_crm: Globe, marketing: Megaphone, pro: Sparkles, business: Star, elite: Crown };
const ACCENT = { web_crm: '#2F5E96', marketing: '#7A0B7A', pro: '#006400', business: '#0f4c81', elite: '#D4A24B' };

export function perkLines(t, features) {
  const lines = [];
  if (t.lead_price != null) lines.push({ on: true, text: `£${Number(t.lead_price).toFixed(0)} per accepted lead (standard £40)` });
  if (t.free_leads_month) lines.push({ on: true, text: `${t.free_leads_month} lead${t.free_leads_month === 1 ? '' : 's'} included every month` });
  Object.entries(features).forEach(([k, label]) => {
    const on = !!t.features?.[k];
    if (k === 'ai_captions_unlimited' && !on) lines.push({ on: false, text: t.ai_captions_month != null ? `${t.ai_captions_month} AI caption posts a month` : label, soft: true });
    else lines.push({ on, text: label });
  });
  return lines;
}

function TierCard({ t, yearly, isCurrent, canSubscribe, busy, onPick, features, currentOrder }) {
  const paid = t.price_gbp > 0;
  const Icon = ICON[t.id];
  const accent = ACCENT[t.id] || '#27292E';
  const price = yearly ? t.yearly_price_gbp : t.price_gbp;
  const isUpgrade = t.order > currentOrder;
  return (
    <Card className={`p-5 flex flex-col relative overflow-hidden ${t.id === 'web_crm' ? 'border-2 shadow-lg' : ''}`} style={t.id === 'web_crm' ? { borderColor: accent } : undefined} data-testid={`tier-card-${t.id}`}>
      {t.id === 'web_crm' && <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider text-white rounded-full px-2 py-0.5" style={{ background: accent }}>Most popular</span>}
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5" style={{ color: accent }} />}
        <h2 className="text-xl font-bold text-[#27292E]">{t.name}</h2>
        {isCurrent && <span className="text-xs font-bold bg-[#006400] text-white rounded-full px-2.5 py-1 ml-auto" data-testid={`tier-current-${t.id}`}>Current</span>}
      </div>
      <div className="mt-2 text-3xl font-bold text-[#27292E]">{paid ? `£${Number(price).toFixed(2).replace(/\.00$/, '')}` : '£0'}<span className="text-sm font-normal text-slate-500">/{yearly ? 'year' : 'month'}</span></div>
      {paid && Number(t.setup_fee_gbp) > 0 && <div className="text-sm font-semibold text-slate-700" data-testid={`tier-setup-${t.id}`}>+ £{Number(t.setup_fee_gbp).toFixed(2).replace(/\.00$/, '')} one-off setup</div>}
      {paid && yearly && <div className="text-xs text-emerald-700 font-semibold">2 months free vs monthly</div>}
      <p className="text-sm text-slate-600 mt-1">{t.tagline}</p>
      <ul className="mt-4 space-y-1.5 text-sm flex-1" data-testid={`tier-perks-${t.id}`}>
        <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-[#006400] mt-0.5 shrink-0" /> Quotes, projects, calendar, leads, toolbox, training</li>
        {perkLines(t, features).map((l, i) => (
          <li key={i} className={`flex items-start gap-2 ${l.on || l.soft ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${l.on ? 'text-[#006400]' : 'text-slate-300'}`} /> {l.text}
          </li>
        ))}
      </ul>
      {paid && !isCurrent && canSubscribe && (
        <Button onClick={() => onPick(yearly ? t.yearly_lookup_key : t.lookup_key)} disabled={!!busy} className="mt-5 h-11 text-white" style={{ background: accent }} data-testid={`tier-subscribe-${t.id}`}>
          {busy === (yearly ? t.yearly_lookup_key : t.lookup_key) ? 'One moment…' : `${isUpgrade ? 'Upgrade' : 'Switch'} to ${t.name}`}
        </Button>
      )}
      {paid && <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secure card payment by Stripe · cancel any time</p>}
    </Card>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { plan, reload } = useMyPlan();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState('');
  const [yearly, setYearly] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/billing/tiers`).then((r) => r.json()).then(setTiers).catch(() => {});
    if (params.get('cancelled')) toast.message('Checkout cancelled — no charge made.');
  }, [params]);
  useEffect(() => { if (plan?.subscription?.interval === 'year') setYearly(true); }, [plan]);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const d = await post('/billing/checkout', { lookup_key, origin_url: window.location.origin });
      if (d.switched) { toast.success(`You're now on ${d.tier.name} — the difference is prorated on your next invoice.`); await reload(); setBusy(''); return; }
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(e.message); setBusy(''); }
  };
  const act = async (path, ok) => {
    setBusy(path);
    try { await post(path); toast.success(ok); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const portal = async () => {
    setBusy('portal');
    try { const d = await post('/billing/portal', { origin_url: window.location.origin }); window.location.href = d.url; }
    catch (e) { toast.error(e.message); setBusy(''); }
  };

  const current = plan?.tier?.id || 'free';
  const currentOrder = tiers.find((t) => t.id === current)?.order ?? 0;
  const sub = plan?.subscription;
  const features = plan?.features || {};

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <button onClick={() => navigate('/settings')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3" data-testid="billing-back"><ArrowLeft className="w-4 h-4" /> Settings</button>
        <h1 className="text-3xl font-bold text-[#27292E] flex items-center gap-2"><CreditCard className="w-7 h-7 text-[#006400]" /> Your plan</h1>
        <p className="text-slate-600 mt-1 mb-6">Use ProPortal as your CRM for free. Add a website we build and host for you, or let Ox Resin run your marketing as well.</p>

        {plan && (
          <Card className="p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="billing-current">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-bold">Current plan</div>
              <div className="text-2xl font-bold text-[#27292E]" data-testid="billing-current-tier">{plan.tier.name}</div>
              {plan.source === 'staff' && <div className="text-xs text-amber-700 mt-0.5" data-testid="billing-staff-note">Set by Ox Resin staff{plan.note ? ` — ${plan.note}` : ''}</div>}
              {sub && plan.source === 'stripe' && (
                <div className="text-xs text-slate-500 mt-0.5" data-testid="billing-renewal">
                  {sub.cancel_at_period_end ? 'Cancels' : 'Renews'} {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'at period end'}{sub.interval === 'year' ? ' · billed yearly' : ''}
                  {sub.status === 'past_due' && <span className="text-red-600 font-semibold"> · payment failed — update your card</span>}
                </div>
              )}
            </div>
            {sub && plan.source === 'stripe' && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={portal} disabled={!!busy} data-testid="billing-portal">Manage card / invoices</Button>
                {sub.cancel_at_period_end
                  ? <Button size="sm" onClick={() => act('/billing/resume', 'Subscription resumed')} disabled={!!busy} className="bg-[#006400] text-white" data-testid="billing-resume">Keep {plan.tier.name}</Button>
                  : <Button variant="outline" size="sm" onClick={() => window.confirm(`Cancel ${plan.tier.name} at the end of this billing period?`) && act('/billing/cancel', `${plan.tier.name} will end at the period end`)} disabled={!!busy} className="text-red-600 border-red-200" data-testid="billing-cancel">Cancel</Button>}
              </div>
            )}
          </Card>
        )}

        {tiers.some((t) => Number(t.yearly_price_gbp) > 0) && (
        <div className="flex justify-center mb-5">
          <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 text-sm font-semibold" data-testid="billing-interval">
            <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full ${!yearly ? 'bg-[#27292E] text-white' : 'text-slate-600'}`} data-testid="billing-interval-month">Monthly</button>
            <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full ${yearly ? 'bg-[#27292E] text-white' : 'text-slate-600'}`} data-testid="billing-interval-year">Yearly <span className="text-emerald-600 text-xs">save 2 months</span></button>
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {tiers.map((t) => <TierCard key={t.id} t={t} yearly={yearly} isCurrent={t.id === current} canSubscribe={plan?.source !== 'staff'} busy={busy} onPick={subscribe} features={features} currentOrder={currentOrder} />)}
        </div>
      </main>
    </div>
  );
}
